import { useCallback, useSyncExternalStore } from "react";

/**
 * Subscribes to a CSS media query (same string as `window.matchMedia`).
 *
 * @param query - e.g. `"(max-width: 768px)"`, `"(max-height: 760px)"`, `"(prefers-reduced-motion: reduce)"`
 * @returns Whether the query currently matches.
 *
 * During SSR / when `window` is unavailable, returns `false` until the client runs `getSnapshot`.
 */
export const useMediaQuery = (query: string): boolean => {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (typeof window === "undefined") {
        return () => {};
      }

      const mql = window.matchMedia(query);
      mql.addEventListener("change", onStoreChange);

      return () => {
        mql.removeEventListener("change", onStoreChange);
      };
    },
    [query]
  );

  const getSnapshot = useCallback(
    () => (typeof window !== "undefined" ? window.matchMedia(query).matches : false),
    [query]
  );

  return useSyncExternalStore(subscribe, getSnapshot, () => false);
};
